// sim/personality.ts — COLONY PERSONALITY EVOLUTION: emergent temperament.
//
// A Brood's character is not in its genes but in its CHOICES. The autonomy layer
// (colonyAI.ts) picks ONE winning NEED each decision cadence; this layer watches
// those repeated choices — plus two outcome signals (rival kills, priority
// switches) — and accumulates them into a per-colony TRAIT-tendency vector. The
// dominant tendency, once it clears `emergeThreshold`, CRYSTALLIZES into a named
// personality:
//
//   Aggressive · Expansionist · Industrial · Defensive · Adaptive · Predatory
//
// That personality then feeds BACK: `biasFor` returns a SMALL, capped nudge on
// the matching need score, so a colony's history shapes its future priorities — a
// self-reinforcing loop. The bias is deliberately tiny (a few points) and only
// touches the marginal/ambiguous decisions: a real food crisis or an actual
// threat still dwarfs it, so personality NEVER overrides survival.
//
// THE GOAL this enables: two colonies with identical genes/config but different
// circumstances accumulate different trait vectors → emerge as different
// personalities → behave differently. Genes set the body; choices set the soul.
//
// TEMPERAMENT DRIFTS (slow EMA decay): scores do not accumulate forever. On a
// fixed cadence (every DECAY_EVERY recorded decisions) each colony sheds a small
// fraction — `floor(score / decayDivisor)` — of EVERY trait score. That makes the
// vector an exponential-moving-average of recent behaviour: old signal fades while
// recent decisions keep their weight. The constants are tuned so a temperament
// still EMERGES inside a normal run (the per-cadence loss is far smaller than the
// per-decision gain on the trait a colony keeps choosing), yet a colony whose
// behaviour FLIPS mid-life will, over a long run, see its old dominant decay below
// the new one and re-crystallize — a drift the old monotonic model could never do.
// Decay is multiplicative, so scores are bounded for any run length.
//
// DESIGN DISCIPLINE:
//   - Emergent: nothing assigns a personality; it is the argmax of accumulated
//     (then slowly-decayed) decision points, so it can only be earned through play.
//   - Deterministic: pure integer SoA, no RNG (personality is a function of the
//     decision stream), fixed iteration order, exact FNV-foldable. Decay is integer
//     `floor` division on a tick-free per-colony decision count, so it never breaks
//     run-twice-equal.
//   - Allocation-free: `record`/`biasFor` allocate nothing (no temp objects,
//     no closures); the per-decision cost is a handful of array writes, an
//     occasional six-way decay sweep, and a six-way argmax.

import { TRAIT, TRAIT_COUNT, TRAIT_FORMING, NEED, CASTE } from '../types.ts';
import type { ColonyId, Need, Trait, PersonalityConfig } from '../types.ts';

/**
 * Human-readable name per TRAIT.* value (CLI / describe). Order MUST match the
 * TRAIT constant so TRAIT_NAME[trait] is correct.
 */
export const TRAIT_NAME: readonly string[] = [
  'Aggressive',
  'Expansionist',
  'Industrial',
  'Defensive',
  'Adaptive',
  'Predatory',
];

/**
 * One-line identity descriptor per TRAIT.* (UI / CLI flavour). Order matches the
 * TRAIT constant so TRAIT_DESC[trait] is correct.
 */
export const TRAIT_DESC: readonly string[] = [
  'Wages war from strength — a standing army, ground taken',
  'Spreads relentlessly — new hives, fresh frontier',
  'Builds and grows — workers, food, the steady engine',
  'Digs in — fortified, garrisoned, slow to yield',
  'Reads the moment — switches tack, evolves to fit',
  'Hunts to thrive — feeds on rivals and prey',
];

// --- IDENTITY MAPPINGS: how structural signals + an emerged identity translate
//     into trait points and behaviour. All pure integer lookups. ---------------

/**
 * The trait a dominant GENE channel expresses as identity, indexed by
 * EFFECT_CHANNEL.* (Movement, Combat, Sensing, Defense, Toxicity, Regen, Size):
 * stacking Combat reads Aggressive; Toxicity → Predatory; Defense → Defensive;
 * Movement → Expansionist; Regen/Size → Industrial; Sensing → Adaptive.
 */
const CHANNEL_TRAIT: readonly number[] = [
  TRAIT.Expansionist, // Movement
  TRAIT.Aggressive,   // Combat
  TRAIT.Adaptive,     // Sensing
  TRAIT.Defensive,    // Defense
  TRAIT.Predatory,    // Toxicity
  TRAIT.Industrial,   // Regen
  TRAIT.Industrial,   // Size
];

/** Per-sample territory points are capped so a sprawling colony can't run away. */
const TERRITORY_CAP = 3;

/**
 * SLOW-DECAY cadence: apply one EMA decay sweep every this-many RECORDED decisions
 * per colony (`recordStructural` adds points but does not tick this clock). Small
 * enough that decay keeps pace with a long run, large enough that a forming colony
 * gets several clean accumulation steps between sheds so a temperament can emerge.
 */
const DECAY_EVERY = 4;

/**
 * Default EMA decay divisor when `PersonalityConfig.decayDivisor` is omitted: each
 * sweep removes `floor(score / 8)` (≈12.5%), a half-life of ~21 decisions. Decay is
 * ON by default so the drifting model is live without touching the shared config.
 * Tuned against the persona census: the dominant trait a colony keeps choosing
 * still clears `emergeThreshold` within a normal run, while a mid-run behaviour
 * flip can carry a NEW trait past the old (now-decaying) one over a longer run.
 */
const DEFAULT_DECAY_DIVISOR = 8;

/**
 * RESOURCE-ALLOCATION doctrine: the caste an IDLE colony of each identity grows
 * once its garrison is satisfied (-1 = the balanced default cycle). Indexed by
 * TRAIT.*. Applied only in the resting state, so it never starves a live need.
 */
const ECONOMIC_CASTE: readonly number[] = [
  CASTE.Worker, // Aggressive   — feed the war machine
  CASTE.Flyer,  // Expansionist — scouts for new ground
  CASTE.Worker, // Industrial   — the labour force
  CASTE.Worker, // Defensive    — sustain the fortress
  -1,           // Adaptive     — no fixed lean (balanced)
  CASTE.Worker, // Predatory    — workers to fuel the hunters
];

/**
 * MILITARY doctrine: the standing-soldier ratio (% of population) each identity
 * keeps even at peace. An idle colony below this target grows Soldiers before its
 * economic caste. Indexed by TRAIT.*.
 */
const SOLDIER_PCT: readonly number[] = [
  55, // Aggressive   — a large standing army
  35, // Expansionist — a mobile escort over fresh ground
  30, // Industrial   — a lean but real guard (still hunts + defends)
  50, // Defensive    — a deep garrison
  38, // Adaptive     — a flexible middle
  48, // Predatory    — a hunting force
];

/**
 * Per-colony emergent-personality store. Colonies are FEW, so this is a compact
 * set of typed arrays keyed by ColonyId — the same data-oriented discipline as
 * the rest of the engine. No per-colony heap objects.
 */
export class ColonyPersonality {
  readonly capacity: number;
  private readonly config: PersonalityConfig;
  /**
   * Resolved EMA decay divisor: `config.decayDivisor` when provided, else
   * DEFAULT_DECAY_DIVISOR (decay is ON by default). A value ≤ 0 disables decay,
   * restoring the legacy monotonic-accumulation model. Cached as a field so the
   * hot path never re-resolves the optional config.
   */
  private readonly decayDivisor: number;

  // --- Per-colony SoA. ---
  /** Trait-tendency scores: index [cid * TRAIT_COUNT + trait]. */
  private readonly score: Int32Array;
  /** The crystallized dominant trait, or TRAIT_FORMING before one emerges. */
  private readonly dominant: Int32Array;
  /** The previously chosen priority (-1 = none yet) — drives the switch signal. */
  private readonly prevPriority: Int32Array;
  /** Kill count seen at the last `record` (so we accrue only the delta). */
  private readonly lastKills: Int32Array;
  /** Recorded decisions since this colony's last decay sweep — the EMA clock. */
  private readonly sinceDecay: Int32Array;

  // --- Public counters (deterministic; foldable into the hash, read by tests). ---
  /** Total decisions recorded across all colonies. */
  recordsTotal = 0;
  /** Total personalities that have crystallized (a forming→emerged transition). */
  emergencesTotal = 0;
  /** Total structural samples (gene + territory) folded across all colonies. */
  structuralTotal = 0;

  constructor(maxColonies: number, config: PersonalityConfig) {
    if (maxColonies <= 0) throw new Error('ColonyPersonality maxColonies must be > 0');
    this.capacity = maxColonies;
    this.config = config;
    // Decay defaults ON: only an explicit numeric override (incl. ≤0 to disable)
    // replaces the module default, so the drifting model is live everywhere the
    // shared default config is used without that config having to opt in.
    this.decayDivisor =
      config.decayDivisor === undefined ? DEFAULT_DECAY_DIVISOR : (config.decayDivisor | 0);

    this.score = new Int32Array(maxColonies * TRAIT_COUNT);
    this.dominant = new Int32Array(maxColonies).fill(TRAIT_FORMING);
    this.prevPriority = new Int32Array(maxColonies).fill(-1);
    this.lastKills = new Int32Array(maxColonies);
    this.sinceDecay = new Int32Array(maxColonies);
  }

  /**
   * Record ONE arbitrated decision for colony `cid`. Call AFTER the AI picks its
   * winning `priority`, so decision N is shaped by the personality formed from
   * decisions 1..N-1 (a clean causal loop, no same-tick circularity). Accumulates
   * integer trait points from the choice + outcome signals, applies the periodic
   * EMA decay (every DECAY_EVERY records), then recomputes the dominant trait so a
   * temperament both EMERGES and can later DRIFT as behaviour changes.
   * Allocation-free.
   *
   *   priority Food / Reproduction → Industrial   (the builder/grower temperament)
   *   priority Expansion           → Expansionist
   *   priority Defense             → Aggressive if well-garrisoned (soldier ratio
   *                                  ≥ soldierRatioPct), else Defensive
   *   priority Evolution           → Adaptive
   *   a CHANGE of priority vs last → Adaptive (the colony switches/adapts)
   *   rival kills since last record→ Predatory (scaled by killWeight)
   */
  record(cid: ColonyId, priority: Need, population: number, soldiers: number, kills: number): void {
    const cfg = this.config;
    const base = cid * TRAIT_COUNT;

    // --- Choice → trait points. ---
    if (priority === NEED.Food || priority === NEED.Reproduction) {
      this.score[base + TRAIT.Industrial] =
        (this.score[base + TRAIT.Industrial] as number) + cfg.priorityWeight;
    } else if (priority === NEED.Expansion) {
      this.score[base + TRAIT.Expansionist] =
        (this.score[base + TRAIT.Expansionist] as number) + cfg.priorityWeight;
    } else if (priority === NEED.Defense) {
      // A defended posture reads as Aggressive when the colony already fields a
      // strong garrison (it can take the fight to threats), else Defensive (it is
      // reacting from a position of weakness). Integer compare: avoid division.
      if (soldiers * 100 >= population * cfg.soldierRatioPct) {
        this.score[base + TRAIT.Aggressive] =
          (this.score[base + TRAIT.Aggressive] as number) + cfg.priorityWeight;
      } else {
        this.score[base + TRAIT.Defensive] =
          (this.score[base + TRAIT.Defensive] as number) + cfg.priorityWeight;
      }
    } else if (priority === NEED.Evolution) {
      this.score[base + TRAIT.Adaptive] =
        (this.score[base + TRAIT.Adaptive] as number) + cfg.evolveWeight;
    }

    // --- Outcome signal: a priority SWITCH reads as adaptability. ---
    const prev = this.prevPriority[cid] as number;
    if (prev !== -1 && priority !== prev) {
      this.score[base + TRAIT.Adaptive] =
        (this.score[base + TRAIT.Adaptive] as number) + cfg.switchWeight;
    }

    // --- Outcome signal: rival kills since the last record read as predation. ---
    const prevKills = this.lastKills[cid] as number;
    let killsDelta = kills - prevKills;
    if (killsDelta < 0) killsDelta = 0; // guard a reset/overflow; never go negative
    if (killsDelta > 0) {
      this.score[base + TRAIT.Predatory] =
        (this.score[base + TRAIT.Predatory] as number) + killsDelta * cfg.killWeight;
    }
    this.lastKills[cid] = kills;

    this.prevPriority[cid] = priority;

    // SLOW EMA DECAY: a recorded decision ticks this colony's decay clock; every
    // DECAY_EVERY decisions, shed a fraction of EVERY trait so old behaviour fades
    // while the trait this colony keeps choosing (gained THIS record) stays ahead.
    // (recordStructural deliberately does not advance this clock.)
    if (this.decayDivisor > 0) {
      const since = (this.sinceDecay[cid] as number) + 1;
      if (since >= DECAY_EVERY) {
        this.sinceDecay[cid] = 0;
        this.decay(cid);
      } else {
        this.sinceDecay[cid] = since;
      }
    }

    this.recompute(cid);
    this.recordsTotal++;
  }

  /**
   * Fold the colony's STRUCTURAL signals into its identity: its genes and its
   * claimed territory, alongside the decision stream `record` tracks. Call on the
   * same decision cadence (allocation-free):
   *
   *   dominantChannel  the EFFECT_CHANNEL.* the colony most invests its genes in
   *                    (caller computes the argmax of its gene profile), or -1 for
   *                    a colony with no expressed genes → CHANNEL_TRAIT vote.
   *   hives            founded hive nodes (claimed territory) → Expansionist,
   *                    capped per sample so sprawl tilts but never dominates.
   *
   * Genes set the body, territory the reach; both now shape the soul, not just the
   * colony's choices. Pure integer accumulation into the same score vector, so the
   * dominant identity is the argmax across ALL signals.
   */
  recordStructural(cid: ColonyId, dominantChannel: number, hives: number): void {
    const cfg = this.config;
    const base = cid * TRAIT_COUNT;

    // Gene investment → the trait that channel expresses.
    if (dominantChannel >= 0 && dominantChannel < CHANNEL_TRAIT.length) {
      const trait = CHANNEL_TRAIT[dominantChannel] as number;
      this.score[base + trait] = (this.score[base + trait] as number) + cfg.geneWeight;
    }

    // Claimed territory → Expansionist (more nodes ⇒ more, capped).
    if (hives > 0) {
      const t = hives > TERRITORY_CAP ? TERRITORY_CAP : hives;
      this.score[base + TRAIT.Expansionist] =
        (this.score[base + TRAIT.Expansionist] as number) + t * cfg.territoryWeight;
    }

    this.recompute(cid);
    this.structuralTotal++;
  }

  /**
   * The FEEDBACK: the bias to add to `need`'s score this decision, given the
   * colony's emerged personality. Returns 0 while still forming, or for any need
   * the dominant trait does not push. Small + capped by `biasStrength` so it only
   * tips marginal calls — survival needs (a food crisis, a real threat) dwarf it.
   * Allocation-free, branch-light.
   */
  biasFor(cid: ColonyId, need: Need): number {
    const dom = this.dominant[cid] as number;
    if (dom === TRAIT_FORMING) return 0;
    const b = this.config.biasStrength;
    const half = (b / 2) | 0; // integer floor(B/2)
    switch (dom) {
      case TRAIT.Aggressive:
        // Pushes the fight: mostly Defense (engage threats), a little Expansion.
        if (need === NEED.Defense) return b;
        if (need === NEED.Expansion) return half;
        return 0;
      case TRAIT.Defensive:
        if (need === NEED.Defense) return b;
        return 0;
      case TRAIT.Expansionist:
        if (need === NEED.Expansion) return b;
        return 0;
      case TRAIT.Industrial:
        if (need === NEED.Food) return b;
        return 0;
      case TRAIT.Predatory:
        // Like Aggressive: leans into combat + grabbing ground from rivals.
        if (need === NEED.Defense) return b;
        if (need === NEED.Expansion) return half;
        return 0;
      case TRAIT.Adaptive:
        if (need === NEED.Evolution) return b;
        return 0;
      default:
        return 0;
    }
  }

  /**
   * One EMA decay sweep for `cid`: every trait sheds `floor(score / decayDivisor)`,
   * clamped ≥ 0. Multiplicative, so scores converge to a finite cap for any run
   * length (gain per cycle balances the fractional loss) — recent behaviour keeps
   * weight, stale signal fades. Pure integer, allocation-free; the caller gates the
   * cadence and guards `decayDivisor > 0`, so this is a flat six-iteration loop.
   */
  private decay(cid: ColonyId): void {
    const base = cid * TRAIT_COUNT;
    const d = this.decayDivisor;
    for (let t = 0; t < TRAIT_COUNT; t++) {
      const s = this.score[base + t] as number;
      // Integer floor toward zero; s is non-negative here, so `/ d | 0` is exact.
      if (s > 0) this.score[base + t] = s - ((s / d) | 0);
    }
  }

  /**
   * Recompute the dominant trait for `cid`: argmax over the six scores with a
   * fixed low-index tie-break, crystallized only once the max clears
   * `emergeThreshold` (else TRAIT_FORMING). Allocation-free.
   */
  private recompute(cid: ColonyId): void {
    const base = cid * TRAIT_COUNT;
    let bestTrait = 0;
    let bestScore = this.score[base] as number;
    for (let t = 1; t < TRAIT_COUNT; t++) {
      const s = this.score[base + t] as number;
      if (s > bestScore) {
        bestScore = s;
        bestTrait = t;
      }
    }
    const wasForming = (this.dominant[cid] as number) === TRAIT_FORMING;
    if (bestScore >= this.config.emergeThreshold) {
      this.dominant[cid] = bestTrait;
      if (wasForming) this.emergencesTotal++;
    } else {
      this.dominant[cid] = TRAIT_FORMING;
    }
  }

  // --- Accessors (hash / CLI / tests) ----------------------------------------

  /** The crystallized dominant trait of `cid`, or TRAIT_FORMING. */
  dominantOf(cid: ColonyId): number {
    return this.dominant[cid] as number;
  }
  /** Current accumulated score for one trait of `cid`. */
  scoreOf(cid: ColonyId, trait: Trait): number {
    return this.score[cid * TRAIT_COUNT + trait] as number;
  }
  /** The last priority `cid` recorded (-1 = none yet). */
  prevPriorityOf(cid: ColonyId): number {
    return this.prevPriority[cid] as number;
  }
  /** True once `cid`'s personality has crystallized (a real trait, not forming). */
  hasEmerged(cid: ColonyId): boolean {
    return (this.dominant[cid] as number) !== TRAIT_FORMING;
  }
  /** Kill count last seen by `cid`'s record (for the Predatory delta). */
  lastKillsOf(cid: ColonyId): number {
    return this.lastKills[cid] as number;
  }

  // --- Identity EFFECTS (read by the AI's idle-state doctrine) ----------------

  /**
   * RESOURCE ALLOCATION: the caste an idle colony of this identity grows (CASTE.*),
   * or -1 for the balanced default cycle / a still-forming colony. The AI consults
   * this only in the resting state, so a live need (food, threat) always wins first.
   */
  economicCaste(cid: ColonyId): number {
    const dom = this.dominant[cid] as number;
    if (dom === TRAIT_FORMING) return -1;
    return (ECONOMIC_CASTE[dom] as number) ?? -1;
  }

  /**
   * MILITARY DOCTRINE: the standing-soldier ratio (% of population) this identity
   * maintains at peace. 0 while forming. The AI grows Soldiers in its idle state
   * until the colony reaches this ratio, then switches to its economic caste.
   */
  soldierDoctrine(cid: ColonyId): number {
    const dom = this.dominant[cid] as number;
    if (dom === TRAIT_FORMING) return 0;
    return (SOLDIER_PCT[dom] as number) ?? 0;
  }
}
